import React from "react";
import { useTranslation } from 'react-i18next';
import { 
  ChatBubbleLeftRightIcon, 
  InformationCircleIcon, 
  QrCodeIcon, 
  UserCircleIcon,
  PhoneIcon,
  QuestionMarkCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from "@heroicons/react/24/outline";

const DashboardSupport = () => {
    const { t } = useTranslation();

    const supportContacts = [
        {
            id: 1,
            name: "Gero",
            role: t('support.generalAssistance'),
            phone: "40742518064",
            whatsappUrl: "https://wa.me/40742518064",
            available: true,
            specialization: t('support.faqAnswer1').split('sau')[0].trim()
        },
        {
            id: 2,
            name: "Stefan", 
            role: t('support.generalAssistance'),
            phone: "40772160690",
            whatsappUrl: "https://wa.me/40772160690",
            available: true,
            specialization: t('support.faqAnswer2').split(',')[0].trim()
        }
    ];

    return (
        <div className="space-y-6 pb-4">
            {/* Page Header */}
            {/* <div className="mb-6">
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                    {t('support.title')}
                </h1>
                <p className="text-slate-600 dark:text-slate-400">
                    {t('support.subtitle')}
                </p>
            </div> */}

            {/* Quick Info Cards */}
            <div className="grid grid-cols-3 md:grid-cols-1 gap-4 mb-6">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                    <div className="flex items-center">
                        <ClockIcon className="h-8 w-8 text-blue-600 dark:text-blue-400 mr-3" />
                        <div>
                            <h3 className="font-medium text-blue-900 dark:text-blue-100">{t('support.workingHours')}</h3>
                            <p className="text-sm text-blue-700 dark:text-blue-300">{t('support.workingHoursValue')}</p>
                        </div>
                    </div>
                </div>
                
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                    <div className="flex items-center">
                        <CheckCircleIcon className="h-8 w-8 text-emerald-600 dark:text-green-400 mr-3" />
                        <div>
                            <h3 className="font-medium text-green-900 dark:text-green-100">{t('support.quickResponse')}</h3>
                            <p className="text-sm text-green-700 dark:text-green-300">{t('support.quickResponseValue')}</p>
                        </div>
                    </div>
                </div>
                
                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
                    <div className="flex items-center">
                        <ChatBubbleLeftRightIcon className="h-8 w-8 text-purple-600 dark:text-purple-400 mr-3" />
                        <div>
                            <h3 className="font-medium text-purple-900 dark:text-purple-100">{t('support.whatsappDirect')}</h3>
                            <p className="text-sm text-purple-700 dark:text-purple-300">{t('support.whatsappDirectValue')}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* WhatsApp Contact Section */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-t-xl">
                    <div className="flex items-center">
                        <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg mr-3">
                            <ChatBubbleLeftRightIcon className="h-6 w-6 text-emerald-600 dark:text-green-400" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                                {t('support.directWhatsappContact')}
                            </h2>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                {t('support.immediateAssistance')}
                            </p>
                        </div>
                    </div>
                </div>
                
                <div className="p-6">
                    {/* Important Notice */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg">
                        <div className="flex items-start">
                            <InformationCircleIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm text-blue-700 dark:text-blue-300 font-medium mb-1">
                                    {t('support.importantInfo')}
                                </p>
                                <ul className="text-sm text-blue-600 dark:text-blue-400 space-y-1">
                                    <li>• {t('support.responseTime')}</li>
                                    <li>• {t('support.outsideHours')}</li>
                                    <li>• {t('support.scanQR')}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    
                    {/* Support Contacts Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {supportContacts.map((contact) => (
                            <div key={contact.id} className="bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-gray-600 rounded-xl overflow-hidden hover:shadow-md transition-shadow duration-300">
                                {/* Contact Header */}
                                <div className="bg-white dark:bg-slate-700 px-5 py-4 border-b border-slate-200 dark:border-gray-600">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center mr-4">
                                                <UserCircleIcon className="h-7 w-7 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                                                    {contact.name}
                                                </h3>
                                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                                    {contact.role}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center">
                                            <div className="w-3 h-3 bg-emerald-500 rounded-full mr-2"></div>
                                            <span className="text-xs text-emerald-600 dark:text-green-400 font-medium">
                                                {t('support.available')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Contact Body */}
                                <div className="p-5">
                                    <div className="text-center mb-4">
                                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                                            {t('support.specialization')}: {contact.specialization}
                                        </p>
                                        
                                        {/* WhatsApp Button */}
                                        <a 
                                            href={contact.whatsappUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center justify-center w-full px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transform transition-all duration-200 hover:scale-[1.02] shadow-md hover:shadow-lg"
                                        >
                                            <PhoneIcon className="h-5 w-5 mr-2" />
                                            {t('support.contactWhatsapp')}
                                        </a>
                                    </div>
                                    
                                    {/* QR Code */}
                                    <div className="flex justify-center">
                                        <div className="bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-gray-600 rounded-xl p-3 shadow-sm">
                                            <div className="relative">
                                                <img 
                                                    src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(contact.whatsappUrl)}&size=120x120&bgcolor=255-255-255&color=000-000-000`}
                                                    alt={`WhatsApp QR Code - ${contact.name}`}
                                                    className="w-28 h-28 rounded-lg"
                                                />
                                                <div className="absolute -top-1 -right-1 p-1 bg-emerald-500 rounded-full shadow-sm">
                                                    <QrCodeIcon className="h-3 w-3 text-white" />
                                                </div>
                                            </div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 text-center mt-2">
                                                {t('support.scanQRCode')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                
                {/* Footer with additional info */}
                <div className="bg-slate-50 dark:bg-slate-700/50 px-6 py-4 border-t border-slate-200 dark:border-slate-700 rounded-b-xl">
                    <div className="flex items-start">
                        <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                <span className="font-medium">{t('support.importantNote')}</span> {t('support.outsideHoursMessage')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <div className="flex items-center">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-3">
                            <QuestionMarkCircleIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                                {t('support.faq')}
                            </h2>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                {t('support.faqSubtitle')}
                            </p>
                        </div>
                    </div>
                </div>
                
                <div className="p-6">
                    <div className="space-y-4">
                        <div className="border border-slate-200 dark:border-gray-600 rounded-lg p-4">
                            <h3 className="font-medium text-slate-900 dark:text-white mb-2">
                                {t('support.faqQuestion1')}
                            </h3>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                {t('support.faqAnswer1')}
                            </p>
                        </div>
                        
                        <div className="border border-slate-200 dark:border-gray-600 rounded-lg p-4">
                            <h3 className="font-medium text-slate-900 dark:text-white mb-2">
                                {t('support.faqQuestion2')}
                            </h3>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                {t('support.faqAnswer2')}
                            </p>
                        </div>
                        
                        <div className="border border-slate-200 dark:border-gray-600 rounded-lg p-4">
                            <h3 className="font-medium text-slate-900 dark:text-white mb-2">
                                {t('support.faqQuestion3')}
                            </h3>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                {t('support.faqAnswer3')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardSupport;